import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

const CERT_IMAGE = "/img/certificate.webp";

const PHOTOS = [
  {
    src: "/img/dana1.webp",
    alt: "Danka Vysoká - profesionálna trénerka",
  },
  {
    src: "/img/dana2.webp",
    alt: "Danka Vysoká - silový tréning",
  },
  {
    src: "/img/dana3.webp",
    alt: "Danka Vysoká - City Fitness Ruzinov",
  },
  {
    src: "/img/dana4.webp",
    alt: "Danka Vysoká - foto s certifikatom",
  },
];

const achievements = [
  "Certifikovaný personal trainer",
  "Odborník na silový tréning",
  "Výživové poradenstvo a jedálniček",
];

export default function About() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [certOpen, setCertOpen] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((idx) => emblaApi?.scrollTo(idx), [emblaApi]);

  // Track selected slide
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Auto-advance every 4 s
  useEffect(() => {
    if (!emblaApi) return;
    const timer = setInterval(() => emblaApi.scrollNext(), 4000);
    return () => clearInterval(timer);
  }, [emblaApi]);

  return (
    <section
      id="o-mne"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ background: "#1C1C2E" }}
    >
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#1C1C2E] to-transparent pointer-events-none" />
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-[#7209B7]/10 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-4"
        >
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#F72585]">
            O mne
          </span>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          data-testid="about-heading"
          className="text-center text-4xl lg:text-5xl font-black uppercase tracking-tight text-white mb-16"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          SPOZNAJ SVOJU TRÉNERKU
        </motion.h2>

        {/* Slider + Text */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch mb-16">
          {/* Photo Slider */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#F72585]/20 to-[#7209B7]/20 rounded-2xl blur-xl scale-105 pointer-events-none" />

              {/* Carousel */}
              <div
                ref={emblaRef}
                className="overflow-hidden rounded-2xl relative"
                data-testid="about-trainer-image"
              >
                <div className="flex touch-pan-y">
                  {PHOTOS.map((photo, i) => (
                    <div key={i} className="flex-[0_0_100%] min-w-0">
                      <img
                        src={photo.src}
                        alt={photo.alt}
                        className="w-full h-[480px] lg:h-[620px] object-cover"
                        style={{ objectPosition: "center top" }}
                        loading={i === 0 ? "eager" : "lazy"}
                      />
                    </div>
                  ))}
                </div>
                {/* Bottom gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C2E]/40 to-transparent pointer-events-none" />
              </div>

              {/* Prev / Next arrows */}
              <button
                onClick={scrollPrev}
                aria-label="Predchádzajúca fotka"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{ background: "rgba(247,37,133,0.85)" }}
              >
                <ChevronLeft size={18} className="text-white" />
              </button>
              <button
                onClick={scrollNext}
                aria-label="Ďalšia fotka"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{ background: "rgba(247,37,133,0.85)" }}
              >
                <ChevronRight size={18} className="text-white" />
              </button>

              {/* Dot indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {PHOTOS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => scrollTo(i)}
                    aria-label={`Fotka ${i + 1}`}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: selectedIndex === i ? "22px" : "8px",
                      height: "8px",
                      background:
                        selectedIndex === i
                          ? "#F72585"
                          : "rgba(255,255,255,0.35)",
                    }}
                  />
                ))}
              </div>

              {/* Floating badge — above dots */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="absolute bottom-16 right-4 backdrop-blur-sm border rounded-xl px-4 py-3 z-10"
                style={{
                  background: "rgba(37,37,64,0.92)",
                  borderColor: "rgba(247,37,133,0.4)",
                }}
              >
                <div
                  className="text-2xl font-black text-[#F72585]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  15+
                </div>
                <div className="text-xs text-zinc-400">Rokov vo fitness</div>
              </motion.div>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7 flex flex-col lg:pt-8"
          >
            <h3
              className="text-2xl lg:text-3xl font-bold text-white mb-3"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Tvoj partner na ceste k silnému a pevnému telu
            </h3>
            <div className="w-16 h-1 bg-gradient-to-r from-[#F72585] to-[#7209B7] rounded-full mb-7" />

            <div
              className="space-y-4 text-zinc-400 leading-relaxed mb-8"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              <p>
                Pohyb je súčasťou môjho života a ako fitness trénerka
                pomáham mojim klientom budovať silu, formovať postavu a cítiť
                sa dobre vo vlastnom tele – bez extrémov a s dôrazom na
                dlhodobé výsledky.
              </p>
              <p>
                Každý tréning prispôsobujem individuálne tak, aby dával zmysel
                tvojmu telu aj životu.
              </p>
              <p className="text-lg font-semibold text-white">
                Nejde len o to, ako vyzeráš. Ide o to, ako sa cítiš.
              </p>
              <p>Začni teraz. Ja ťa tou cestou prevediem.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {achievements.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle size={16} className="text-[#F72585] flex-shrink-0" />
                  <span className="text-sm text-zinc-300">{item}</span>
                </motion.div>
              ))}
            </div>

            {/* Certificate thumbnail — bottom of right column */}
            <div className="mt-auto pt-6" data-testid="about-certificate">
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">
                Certifikát
              </p>
              <button
                onClick={() => setCertOpen(true)}
                data-testid="certificate-open-button"
                className="group relative overflow-hidden rounded-xl block w-full transition-transform duration-300 hover:scale-[1.02]"
                aria-label="Zobraziť certifikát"
                style={{ border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <img
                  src={CERT_IMAGE}
                  alt="Certifikát — Danka Vysoká, Certified Personal Trainer"
                  className="w-full h-40 object-cover"
                  style={{ objectPosition: "center 15%" }}
                />
                <div className="absolute inset-0 bg-[#1C1C2E]/0 group-hover:bg-[#1C1C2E]/50 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 bg-[#F72585] text-white text-sm font-bold px-4 py-2 rounded-full">
                    <ZoomIn size={16} />
                    Zobraziť
                  </div>
                </div>
              </button>
            </div>
          </motion.div>
        </div>

        {/* Lightbox modal */}
        <AnimatePresence>
          {certOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4"
              style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }}
              onClick={() => setCertOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.85, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="relative max-w-2xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setCertOpen(false)}
                  data-testid="certificate-close-button"
                  className="absolute -top-4 -right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{ background: "#F72585" }}
                  aria-label="Zavrieť"
                >
                  <X size={18} className="text-white" />
                </button>
                <img
                  src={CERT_IMAGE}
                  alt="Certifikát — Danka Vysoká, Certified Personal Trainer"
                  className="w-full rounded-2xl shadow-2xl"
                  data-testid="certificate-full-image"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#252540] to-transparent pointer-events-none" />
    </section>
  );
}
